import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import './ImageLibraryModal.css';
import LottieLoader from '../LoadingSpinner/LottieLoader';
import useInView from '../../hooks/useInView';
import { getLibrarySearchScore } from '../../utils/librarySearch';

import openmojiColour from '../../assets/openmoji_colour.json';
import openmojiOutline from '../../assets/openmoji_outline.json';

import fluentEmojiColour from '../../assets/FluentEmojiColour.json';
import fluentEmojiOutline from '../../assets/FluentEmojiOutline.json';

import cursorDefault from '../../assets/cursors/cursor-default.png';
import cursorText from '../../assets/cursors/cursor-text.png';
import cursorPointer from '../../assets/cursors/cursor-pointer.png';

const getSourceData = (imageSource) => {
  switch (imageSource) {
    case 'OpenMoji Colour':
      return openmojiColour;

    case 'OpenMoji Outline':
      return openmojiOutline;

    case 'Fluent Emoji Colour':
      return fluentEmojiColour;

    case 'Fluent Emoji Outline':
      return fluentEmojiOutline;

    default:
      return [];
  }
};

const getSourceBaseFolder = (imageSource, category) => {
  if (imageSource === 'EDU Groove') {
    return `/Icons/${category}/`;
  }

  if (imageSource.startsWith('OpenMoji ')) {
    const styleFolder = imageSource.replace('OpenMoji ', '');
    return `/OpenMoji/${styleFolder}/`;
  }

  if (imageSource.startsWith('Fluent Emoji ')) {
    const styleFolder = imageSource.replace('Fluent Emoji ', '');
    return `/FluentEmoji/${styleFolder}/`;
  }

  return '';
};

const ImageLibraryModal = ({
  onClose,
  onImageSelect,
  logoUrl,
  squareLogoUrl,
  initialCategory = 'All',
}) => {
  // ---- membership: read from localStorage ----
  let storedUser = {};

  try {
    storedUser =
      JSON.parse(localStorage.getItem('edugroove_user')) || {};
  } catch (_) {
    storedUser = {};
  }

  const isFreeUser =
    storedUser?.membership === 'free' ||
    (
      Array.isArray(storedUser?.roles) &&
      storedUser.roles.includes('armember_basic')
    );

  // ---- allowed sources based on membership ----
  const ALL_SOURCES = [
    'EDU Groove',
    'OpenMoji Colour',
    'OpenMoji Outline',
    'Fluent Emoji Colour',
    'Fluent Emoji Outline',
  ];

  const ALLOWED_SOURCES = isFreeUser
    ? ['EDU Groove']
    : ALL_SOURCES;

  const [imageSource, setImageSource] = useState(
    ALLOWED_SOURCES[0]
  );

  const [selectedCategory, setSelectedCategory] = useState(
    initialCategory || 'All'
  );

  const [images, setImages] = useState([]);
  const [allSourceImages, setAllSourceImages] = useState([]);
  const [imageCache, setImageCache] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [preserveWhiteFill] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState(['All']);

  const modalCursorDefault =
    `url(${cursorDefault}) 2 2, default`;

  const modalCursorPointer =
    `url(${cursorPointer}) 6 2, pointer`;

  const modalCursorText =
    `url(${cursorText}) 0 0, text`;

  useEffect(() => {
    if (!ALLOWED_SOURCES.includes(imageSource)) {
      setImageSource(ALLOWED_SOURCES[0]);
      setSelectedCategory('All');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreeUser]);

  useEffect(() => {
    setSelectedCategory(initialCategory || 'All');
  }, [initialCategory]);

  useEffect(() => {
    const loadCategories = async () => {
      if (imageSource === 'EDU Groove') {
        try {
          const response = await fetch(
            `${process.env.PUBLIC_URL}/Icons/index.json`
          );

          const data = await response.json();

          const sorted = (data.categories || [])
            .filter((category) => category !== 'Your Resources')
            .sort((a, b) => a.localeCompare(b));

          setCategories([
            'All',
            'Your Resources',
            ...sorted,
          ]);
        } catch (error) {
          console.error(
            'Failed to load EDU Groove categories:',
            error
          );

          setCategories(['All']);
        }

        return;
      }

      const data = getSourceData(imageSource);

      const uniqueCategories = [
        ...new Set(
          data.map(
            (image) => image.category || 'Uncategorised'
          )
        ),
      ];

      setCategories([
        'All',
        ...uniqueCategories.sort((a, b) =>
          a.localeCompare(b)
        ),
      ]);
    };

    loadCategories();
  }, [imageSource]);

  const buildCategoryCounts = useCallback(async () => {
    try {
      const counts = {};
      let total = 0;

      if (imageSource === 'EDU Groove') {
        const categoriesResponse = await fetch(
          `${process.env.PUBLIC_URL}/Icons/index.json`
        );

        const categoriesData =
          await categoriesResponse.json();

        const categoriesToCount = [
          'Your Resources',
          ...(categoriesData.categories || []).filter(
            (category) =>
              category !== 'Your Resources'
          ),
        ];

        for (const category of categoriesToCount) {
          if (category === 'Your Resources') {
            let resourceCount = 0;

            if (logoUrl) {
              resourceCount += 1;
            }

            if (squareLogoUrl) {
              resourceCount += 1;
            }

            counts[category] = resourceCount;
            total += resourceCount;

            continue;
          }

          try {
            const response = await fetch(
              `${process.env.PUBLIC_URL}/Icons/${category}/index.json`
            );

            const categoryImages = await response.json();

            const count = Array.isArray(categoryImages)
              ? categoryImages.length
              : 0;

            counts[category] = count;
            total += count;
          } catch (error) {
            console.warn(
              `Failed to count images for category: ${category}`,
              error
            );

            counts[category] = 0;
          }
        }
      } else {
        const data = getSourceData(imageSource);

        data.forEach((image) => {
          const category =
            image.category || 'Uncategorised';

          counts[category] =
            (counts[category] || 0) + 1;

          total += 1;
        });
      }

      setCategoryCounts(counts);
      setTotalCount(total);
    } catch (error) {
      console.error(
        'Failed to build category counts:',
        error
      );

      setCategoryCounts({});
      setTotalCount(0);
    }
  }, [
    imageSource,
    logoUrl,
    squareLogoUrl,
  ]);

  const fetchImages = useCallback(async (category) => {
    setIsLoading(true);

    try {
      let loadedImages = [];

      if (imageSource === 'EDU Groove') {
        if (category === 'Your Resources') {
          const resourceImages = [];

          if (logoUrl) {
            resourceImages.push({
              name: 'School Logo Rectangle',
              file: logoUrl,
              category: 'Your Resources',
              tags: [
                'logo',
                'school',
                'rectangle',
                'wide',
                'resource',
              ],
              isUserResource: true,
            });
          }

          if (squareLogoUrl) {
            resourceImages.push({
              name: 'School Logo Square',
              file: squareLogoUrl,
              category: 'Your Resources',
              tags: [
                'logo',
                'school',
                'square',
                'resource',
              ],
              isUserResource: true,
            });
          }

          loadedImages = resourceImages;
        } else if (category === 'All') {
          const categoriesResponse = await fetch(
            `${process.env.PUBLIC_URL}/Icons/index.json`
          );

          const categoriesData =
            await categoriesResponse.json();

          const categoriesToFetch = (
            categoriesData.categories || []
          )
            .filter(
              (item) => item !== 'Your Resources'
            )
            .sort((a, b) => a.localeCompare(b));

          const results = await Promise.all(
            categoriesToFetch.map(async (item) => {
              try {
                const response = await fetch(
                  `${process.env.PUBLIC_URL}/Icons/${item}/index.json`
                );

                const categoryImages =
                  await response.json();

                if (!Array.isArray(categoryImages)) {
                  return [];
                }

                return categoryImages.map((image) => ({
                  ...image,
                  category: item,
                }));
              } catch (error) {
                console.warn(
                  `Failed to load images for category: ${item}`,
                  error
                );

                return [];
              }
            })
          );

          const allLibraryImages = results.flat();
          const resourceImages = [];

          if (logoUrl) {
            resourceImages.push({
              name: 'School Logo Rectangle',
              file: logoUrl,
              category: 'Your Resources',
              tags: [
                'logo',
                'school',
                'rectangle',
                'wide',
                'resource',
              ],
              isUserResource: true,
            });
          }

          if (squareLogoUrl) {
            resourceImages.push({
              name: 'School Logo Square',
              file: squareLogoUrl,
              category: 'Your Resources',
              tags: [
                'logo',
                'school',
                'square',
                'resource',
              ],
              isUserResource: true,
            });
          }

          loadedImages = [
            ...resourceImages,
            ...allLibraryImages,
          ];
        } else {
          const response = await fetch(
            `${process.env.PUBLIC_URL}/Icons/${category}/index.json`
          );

          const categoryImages = await response.json();

          loadedImages = Array.isArray(categoryImages)
            ? categoryImages.map((image) => ({
                ...image,
                category,
              }))
            : [];
        }
      } else {
        const data = getSourceData(imageSource);

        loadedImages =
          category === 'All'
            ? data.map((image) => ({
                ...image,
                category:
                  image.category || 'Uncategorised',
              }))
            : data
                .filter(
                  (image) =>
                    (
                      image.category ||
                      'Uncategorised'
                    ) === category
                )
                .map((image) => ({
                  ...image,
                  category:
                    image.category || 'Uncategorised',
                }));
      }

      let sortedImages = [];

      if (category === 'All') {
        sortedImages = [...loadedImages].sort(
          (a, b) => {
            const categoryA = String(
              a.category || ''
            ).toLowerCase();

            const categoryB = String(
              b.category || ''
            ).toLowerCase();

            if (categoryA !== categoryB) {
              return categoryA.localeCompare(categoryB);
            }

            const nameA = String(
              a.name || ''
            ).toLowerCase();

            const nameB = String(
              b.name || ''
            ).toLowerCase();

            return nameA.localeCompare(nameB);
          }
        );
      } else {
        sortedImages = [...loadedImages].sort(
          (a, b) =>
            String(a.name || '').localeCompare(
              String(b.name || '')
            )
        );
      }

      const cacheKey =
        `${imageSource}_${category}`;

      setImageCache((previous) => ({
        ...previous,
        [cacheKey]: sortedImages,
      }));

      setImages(sortedImages);
    } catch (error) {
      console.error(
        'Failed to load images:',
        error
      );

      setImages([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    imageSource,
    logoUrl,
    squareLogoUrl,
  ]);

  useEffect(() => {
    const cacheKey =
      `${imageSource}_${selectedCategory}`;

    if (imageCache[cacheKey]) {
      setImages(imageCache[cacheKey]);
    } else {
      fetchImages(selectedCategory);
    }
  }, [
    selectedCategory,
    imageSource,
    imageCache,
    fetchImages,
  ]);

  const fetchAllSourceImages = useCallback(async () => {
    try {
      let loadedImages = [];

      if (imageSource === 'EDU Groove') {
        const categoriesResponse = await fetch(
          `${process.env.PUBLIC_URL}/Icons/index.json`
        );

        if (!categoriesResponse.ok) {
          throw new Error(
            `Failed to load image categories: ${categoriesResponse.status}`
          );
        }

        const categoriesData =
          await categoriesResponse.json();

        const categoriesToFetch = (
          categoriesData.categories || []
        )
          .filter(
            (category) =>
              category !== 'Your Resources'
          )
          .sort((a, b) => a.localeCompare(b));

        const results = await Promise.all(
          categoriesToFetch.map(async (category) => {
            try {
              const response = await fetch(
                `${process.env.PUBLIC_URL}/Icons/${category}/index.json`
              );

              if (!response.ok) {
                throw new Error(
                  `Failed to load ${category}: ${response.status}`
                );
              }

              const categoryImages =
                await response.json();

              if (!Array.isArray(categoryImages)) {
                return [];
              }

              return categoryImages.map((image) => ({
                ...image,
                category,
              }));
            } catch (error) {
              console.warn(
                `Failed to load search images for ${category}`,
                error
              );

              return [];
            }
          })
        );

        const resourceImages = [];

        if (logoUrl) {
          resourceImages.push({
            name: 'School Logo Rectangle',
            file: logoUrl,
            category: 'Your Resources',
            tags: [
              'logo',
              'school',
              'rectangle',
              'wide',
              'resource',
            ],
            isUserResource: true,
          });
        }

        if (squareLogoUrl) {
          resourceImages.push({
            name: 'School Logo Square',
            file: squareLogoUrl,
            category: 'Your Resources',
            tags: [
              'logo',
              'school',
              'square',
              'resource',
            ],
            isUserResource: true,
          });
        }

        loadedImages = [
          ...resourceImages,
          ...results.flat(),
        ];
      } else {
        const sourceData =
          getSourceData(imageSource);

        loadedImages = sourceData.map((image) => ({
          ...image,
          category:
            image.category || 'Uncategorised',
        }));
      }

      setAllSourceImages(loadedImages);
    } catch (error) {
      console.error(
        'Failed to load complete image source for search:',
        error
      );

      setAllSourceImages([]);
    }
  }, [
    imageSource,
    logoUrl,
    squareLogoUrl,
  ]);

  useEffect(() => {
    buildCategoryCounts();
    fetchAllSourceImages();
  }, [
    buildCategoryCounts,
    fetchAllSourceImages,
  ]);

  const getImageSearchScore = useCallback(
    (image) => {
      const tags = Array.isArray(image.tags)
        ? image.tags
        : typeof image.tags === 'string'
          ? image.tags.split(',')
          : [];

      const imageCategories = Array.isArray(
        image.categories
      )
        ? image.categories
        : typeof image.categories === 'string'
          ? image.categories.split(',')
          : [];

      return getLibrarySearchScore(searchTerm, [
        {
          value: image.name,
          weight: 1,
        },

        ...tags.map((tag) => ({
          value: tag,
          weight: 0.85,
        })),

        {
          value: image.category,
          weight: 0.45,
        },

        ...imageCategories.map((category) => ({
          value: category,
          weight: 0.45,
        })),
      ]);
    },
    [searchTerm]
  );

  const searchedSourceImages = useMemo(() => {
    const trimmedSearch =
      String(searchTerm || '').trim();

    const hasSearch =
      trimmedSearch.length > 0;

    if (!hasSearch) {
      return allSourceImages;
    }

    return allSourceImages
      .map((image, originalIndex) => ({
        image,
        originalIndex,
        score: getImageSearchScore(image),
      }))
      .filter(({ score }) => score >= 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const nameA = String(
          a.image.name || ''
        ).toLowerCase();

        const nameB = String(
          b.image.name || ''
        ).toLowerCase();

        return nameA.localeCompare(nameB);
      })
      .map(({ image }) => image);
  }, [
    allSourceImages,
    getImageSearchScore,
    searchTerm,
  ]);

  const filteredImages = useMemo(() => {
    const hasSearch =
      String(searchTerm || '').trim().length > 0;

    if (!hasSearch) {
      return images;
    }

    if (selectedCategory === 'All') {
      return searchedSourceImages;
    }

    return searchedSourceImages.filter(
      (image) =>
        image.category === selectedCategory
    );
  }, [
    images,
    searchedSourceImages,
    selectedCategory,
    searchTerm,
  ]);

  const visibleCategories = useMemo(() => {
    const hasSearch =
      String(searchTerm || '').trim().length > 0;

    if (!hasSearch) {
      return categories;
    }

    const matchingCategories = new Set(
      searchedSourceImages
        .map((image) => image.category)
        .filter(Boolean)
    );

    return [
      'All',
      ...categories.filter(
        (category) =>
          category !== 'All' &&
          matchingCategories.has(category)
      ),
    ];
  }, [
    categories,
    searchedSourceImages,
    searchTerm,
  ]);

  const visibleCategoryCounts = useMemo(() => {
    const hasSearch =
      String(searchTerm || '').trim().length > 0;

    if (!hasSearch) {
      return categoryCounts;
    }

    const counts = {};

    searchedSourceImages.forEach((image) => {
      const category =
        image.category || 'Uncategorised';

      counts[category] =
        (counts[category] || 0) + 1;
    });

    return counts;
  }, [
    categoryCounts,
    searchedSourceImages,
    searchTerm,
  ]);

  const visibleTotalCount = useMemo(() => {
    const hasSearch =
      String(searchTerm || '').trim().length > 0;

    return hasSearch
      ? searchedSourceImages.length
      : totalCount;
  }, [
    searchedSourceImages,
    searchTerm,
    totalCount,
  ]);

  useEffect(() => {
    if (
      selectedCategory !== 'All' &&
      !visibleCategories.includes(
        selectedCategory
      )
    ) {
      setSelectedCategory('All');
    }
  }, [
    selectedCategory,
    visibleCategories,
  ]);

  return (
    <div
      className="image-library-modal-overlay"
      onClick={onClose}
      style={{
        cursor: modalCursorDefault,
      }}
    >
      <div
        className="image-library-modal"
        onClick={(event) =>
          event.stopPropagation()
        }
        style={{
          cursor: modalCursorDefault,
        }}
      >
        <div className="image-library-header">
          <h2>Image Library</h2>

          <button
            onClick={onClose}
            className="image-library-close-btn"
            aria-label="Close Modal"
            style={{
              cursor: modalCursorPointer,
            }}
          >
            ×
          </button>
        </div>

        <div className="image-library-search-bar">
          <input
            className="image-library-search-input"
            type="search"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedCategory('All');
            }}
            aria-label="Search images"
            title={
              'Search tips: "exact phrase", dog|puppy, animal -cartoon'
            }
            autoComplete="off"
            spellCheck="false"
            style={{
              cursor: modalCursorText,
            }}
          />

          <select
            className="image-library-source-select"
            value={imageSource}
            onChange={(event) => {
              const nextSource =
                event.target.value;

              setImageSource(nextSource);
              setSelectedCategory('All');
              setImages([]);
              setAllSourceImages([]);
              setImageCache({});
            }}
            disabled={
              ALLOWED_SOURCES.length === 1
            }
            aria-label="Select image library source"
            style={{
              cursor:
                ALLOWED_SOURCES.length === 1
                  ? modalCursorDefault
                  : modalCursorPointer,
            }}
          >
            {ALLOWED_SOURCES.map((source) => (
              <option
                key={source}
                value={source}
              >
                {source}
              </option>
            ))}
          </select>

          <div
            className="image-library-results-count"
            aria-live="polite"
          >
            {filteredImages.length === 1
              ? '1 image found'
              : `${filteredImages.length} images found`}
          </div>
        </div>

        <div className="image-library-content">
          <div className="image-library-sidebar">
            <ul>
              {visibleCategories.map(
                (category) => {
                  const count =
                    category === 'All'
                      ? visibleTotalCount
                      : (
                          visibleCategoryCounts[
                            category
                          ] || 0
                        );

                  return (
                    <li key={category}>
                      <button
                        className={
                          `image-library-category-btn ${
                            selectedCategory ===
                            category
                              ? 'active'
                              : ''
                          }`
                        }
                        onClick={() =>
                          setSelectedCategory(
                            category
                          )
                        }
                        style={{
                          cursor:
                            modalCursorPointer,
                        }}
                      >
                        {category} ({count})
                      </button>
                    </li>
                  );
                }
              )}
            </ul>
          </div>

          <div className="image-library-grid-wrapper">
            <div className="image-library-grid">
              {filteredImages.length > 0 ? (
                filteredImages.map(
                  (image, index) => (
                    <LazyImageCard
                      key={
                        `${image.name}-${image.file}-${index}`
                      }
                      image={image}
                      imageSource={imageSource}
                      preserveWhiteFill={
                        preserveWhiteFill
                      }
                      onImageSelect={
                        onImageSelect
                      }
                      selectedCategory={
                        selectedCategory
                      }
                      modalCursorPointer={
                        modalCursorPointer
                      }
                    />
                  )
                )
              ) : (
                !isLoading && (
                  <p>
                    No images match your search.
                  </p>
                )
              )}
            </div>

            {isLoading && (
              <div className="image-library-loader-overlay">
                <LottieLoader
                  message="Loading Images..."
                  fullscreen={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLibraryModal;

// ------------------------------
// Lazy-loaded Image Card
// ------------------------------
const LazyImageCard = ({
  image,
  imageSource,
  preserveWhiteFill,
  onImageSelect,
  selectedCategory,
  modalCursorPointer,
}) => {
  const [ref, isInView] = useInView({
    threshold: 0.1,
  });

  const [isAdded, setIsAdded] =
    useState(false);

  useEffect(() => {
    if (!isAdded) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setIsAdded(false);
    }, 550);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isAdded]);

  const triggerAddedAnimation = () => {
    setIsAdded(false);

    window.requestAnimationFrame(() => {
      setIsAdded(true);
    });
  };

  const fitImageToImportSize = (
    originalWidth,
    originalHeight,
    importShortSide = 100
  ) => {
    const width = Number(originalWidth);
    const height = Number(originalHeight);

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return {
        width: importShortSide,
        height: importShortSide,
      };
    }

    const scale =
      importShortSide / Math.min(width, height);

    return {
      width: width * scale,
      height: height * scale,
    };
  };

  const getSvgImportSize = (svgElement) => {
    const viewBox = svgElement
      .getAttribute('viewBox')
      ?.trim()
      .split(/[\s,]+/)
      .map(Number);

    if (
      viewBox?.length === 4 &&
      Number.isFinite(viewBox[2]) &&
      Number.isFinite(viewBox[3]) &&
      viewBox[2] > 0 &&
      viewBox[3] > 0
    ) {
      return fitImageToImportSize(
        viewBox[2],
        viewBox[3]
      );
    }

    return fitImageToImportSize(
      parseFloat(svgElement.getAttribute('width')),
      parseFloat(svgElement.getAttribute('height'))
    );
  };

  const getRasterImportSize = (src) =>
    new Promise((resolve) => {
      const sourceImage = new Image();

      sourceImage.onload = () => {
        resolve(
          fitImageToImportSize(
            sourceImage.naturalWidth,
            sourceImage.naturalHeight
          )
        );
      };

      sourceImage.onerror = () => {
        resolve({ width: 100, height: 100 });
      };

      sourceImage.src = src;
    });

  const buildImagePayload = async () => {
    const isUserResource =
      image.isUserResource === true;

    const baseFolder =
      getSourceBaseFolder(
        imageSource,
        image.category
      );

    const url = isUserResource
      ? image.file
      : `${process.env.PUBLIC_URL}${baseFolder}${image.file}`;

    const isSvg =
      !isUserResource &&
      image.file &&
      image.file.toLowerCase().endsWith('.svg');

    if (isSvg) {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to load SVG: ${response.status}`
        );
      }

      const svgText = await response.text();

      const parser = new DOMParser();

      const svgDocument = parser.parseFromString(
        svgText,
        'image/svg+xml'
      );

      const parserError =
        svgDocument.querySelector(
          'parsererror'
        );

      if (parserError) {
        throw new Error(
          `Invalid SVG file: ${image.file}`
        );
      }

      const svgElement =
        svgDocument.documentElement;

      if (!svgElement.getAttribute('xmlns')) {
        svgElement.setAttribute(
          'xmlns',
          'http://www.w3.org/2000/svg'
        );
      }

      if (!preserveWhiteFill) {
        const elements =
          svgDocument.querySelectorAll(
            '[fill], [style]'
          );

        elements.forEach((element) => {
          const fill =
            element.getAttribute('fill');

          const style =
            element.getAttribute('style');

          if (
            fill &&
            [
              '#fff',
              '#ffffff',
              'white',
            ].includes(fill.toLowerCase())
          ) {
            element.setAttribute(
              'fill',
              'transparent'
            );
          }

          if (
            style &&
            /fill\s*:\s*(white|#fff|#ffffff)/gi.test(
              style
            )
          ) {
            element.setAttribute(
              'style',
              style.replace(
                /fill\s*:\s*(white|#fff|#ffffff)/gi,
                'fill:transparent'
              )
            );
          }
        });

        const styleTags =
          svgDocument.querySelectorAll(
            'style'
          );

        styleTags.forEach((styleTag) => {
          styleTag.textContent =
            styleTag.textContent.replace(
              /fill\s*:\s*(white|#fff|#ffffff)/gi,
              'fill:transparent'
            );
        });
      }

      const serializer =
        new XMLSerializer();

      const processedSvg =
        serializer.serializeToString(
          svgElement
        );

      const encoded = encodeURIComponent(
        `<?xml version="1.0"?>${processedSvg}`
      );

      const dataUrl =
        `data:image/svg+xml;charset=utf-8,${encoded}`;

      const importSize =
        getSvgImportSize(svgElement);

      return {
        src: dataUrl,
        name: image.name,
        width: importSize.width,
        height: importSize.height,
        meta: {
          svgOriginal: svgText,
          preserveWhiteFill,
          fillColor: null,
          strokeColor: '#000000',
          isSvg: true,
          libraryCategory:
            image.category ||
            selectedCategory ||
            'All',
          libraryName:
            image.name || '',
          imageSource,
        },
      };
    }

    if (isUserResource) {
      const isSquareLogo =
        image.name ===
          'School Logo Square' ||
        (
          Array.isArray(image.tags) &&
          image.tags.includes('square')
        );

      return {
        src: isSquareLogo
          ? '__USER_SQUARE_LOGO__'
          : '__USER_LOGO__',
        name: image.name,
        width: isSquareLogo ? 200 : 600,
        height: isSquareLogo ? 200 : 150,
        isLogoPlaceholder: true,
      };
    }

    const importSize =
      await getRasterImportSize(url);

    return {
      src: url,
      name: image.name,
      width: importSize.width,
      height: importSize.height,
    };
  };

  const handleClick = async () => {
    try {
      const payload =
        await buildImagePayload();

      onImageSelect(payload);
      triggerAddedAnimation();
    } catch (error) {
      console.error(
        'Error adding image from library:',
        error
      );
    }
  };

  const isUserResource =
    image.isUserResource === true;

  const baseFolder =
    getSourceBaseFolder(
      imageSource,
      image.category
    );

  const previewSrc = isUserResource
    ? image.file
    : `${process.env.PUBLIC_URL}${baseFolder}${image.file}`;

  return (
    <div
      ref={ref}
      className={
        `image-library-card ${
          isAdded
            ? 'image-library-card-added'
            : ''
        }`
      }
      onClick={handleClick}
      style={{
        cursor: modalCursorPointer,
      }}
    >
      <p className="image-library-name">
        {image.name}
      </p>

      {isInView && (
        <img
          src={previewSrc}
          alt={image.name}
          className={`image-library-image fade-in ${
            imageSource.startsWith('Fluent Emoji')
              ? 'image-library-image-fluent'
              : ''
          }`}
        />
      )}

      {isAdded && (
        <>
          <div className="image-add-success-ring" />

          <div className="image-add-success-label">
            Added
          </div>

          {Array.from({
            length: 8,
          }).map((_, index) => (
            <span
              key={index}
              className={
                `image-add-success-particle image-add-success-particle-${
                  index + 1
                }`
              }
            />
          ))}
        </>
      )}
    </div>
  );
};
